import React, { useState, memo, useEffect, useCallback, useRef } from 'react';
import {
    Stack,
    Group,
    TextInput,
    Textarea,
    NumberInput,
    Switch,
    Button,
    Alert,
    Title,
    Text,
    Tabs,
    Divider,
    ActionIcon,
    Box,
    Container
} from '@mantine/core';
import { IconDeviceFloppy, IconInfoCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import { Contact, LanSettingsPayload } from 'types/intra';
import { formatDate } from './intraUtils';

/**
 * Props for the LanSettingsForm component.
 */
interface LanSettingsFormProps {
    /** Current draft settings being edited (local state) */
    draftSettings: LanSettingsPayload | null;
    /** Last saved settings from the server (for comparison/info display) */
    lanSettings: LanSettingsPayload | null;
    /** Function to check if an async operation is in progress for a given key */
    isOperating: (key: string) => boolean;
    /** Callback when a settings field value changes */
    onFieldChange: (field: keyof LanSettingsPayload, value: string | number | boolean | Contact[]) => void;
    /** Callback to submit and save the current draft settings */
    onSubmit: () => Promise<void>;
}

/**
 * Form component for editing LAN event settings.
 *
 * Allows organizers to configure:
 * - Event name and total capacity
 * - Start and end dates
 * - Internal announcement/memo for organizers
 * - Feature toggles (registration, bookings, attendance per day, food service)
 *
 * Displays last saved version info with creation and update timestamps.
 *
 * @param props - The component props
 * @returns A settings form or info alert if settings couldn't be loaded
 */
const LanSettingsForm: React.FC<LanSettingsFormProps> = ({
    draftSettings,
    lanSettings,
    isOperating,
    onFieldChange,
    onSubmit
}) => {
    const [activeTab, setActiveTab] = useState<string | null>('basic');
    const [localSettings, setLocalSettings] = useState<LanSettingsPayload | null>(draftSettings);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const lastSyncedRef = useRef<string>('');

    // Sync local state when draftSettings changes externally (e.g., after save)
    useEffect(() => {
        if (draftSettings) {
            const currentKey = JSON.stringify(draftSettings);
            if (currentKey !== lastSyncedRef.current) {
                setLocalSettings(draftSettings);
                lastSyncedRef.current = currentKey;
            }
        }
    }, [draftSettings]);

    if (!localSettings) {
        return <Alert icon={<IconInfoCircle size={16} />} color="blue">Asetuksia ei voitu ladata.</Alert>;
    }

    // Debounced update to parent
    const updateParent = useCallback((field: keyof LanSettingsPayload, value: string | number | boolean | Contact[]) => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            onFieldChange(field, value);
        }, 300);
    }, [onFieldChange]);

    // Immediate update for local state, debounced for parent
    const handleFieldChange = useCallback((field: keyof LanSettingsPayload, value: string | number | boolean | Contact[]) => {
        setLocalSettings(prev => (prev ? { ...prev, [field]: value } : prev));
        updateParent(field, value);
    }, [updateParent]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Clear any pending debounced updates and sync immediately
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        // Update parent with current local state before submit
        // Force immediate sync by calling onFieldChange for all fields
        // This ensures parent has latest state before submit
        if (localSettings) {
            Object.entries(localSettings).forEach(([key, value]) => {
                onFieldChange(key as keyof LanSettingsPayload, value as any);
            });
        }
        // Small delay to ensure state is synced before submit
        setTimeout(() => {
            onSubmit();
        }, 0);
    };

    const handleContactChange = (index: number, field: keyof Contact, value: string | number | boolean) => {
        const contacts = [...(localSettings.contacts || [])];
        contacts[index] = { ...contacts[index], [field]: value };
        handleFieldChange('contacts', contacts);
    };

    const handleAddContact = () => {
        const contacts = [...(localSettings.contacts || [])];
        contacts.push({
            role: 'contact',
            name: '',
            displayOrder: contacts.length + 1,
            isPublic: true
        });
        handleFieldChange('contacts', contacts);
    };

    const handleRemoveContact = (index: number) => {
        const contacts = [...(localSettings.contacts || [])];
        contacts.splice(index, 1);
        handleFieldChange('contacts', contacts);
    };

    return (
        <Stack gap="md" className="lan-settings-form">
            <form onSubmit={handleSubmit}>
                <Tabs value={activeTab} onChange={setActiveTab}>
                    <Tabs.List>
                        <Tabs.Tab value="basic">Perusasetukset</Tabs.Tab>
                        <Tabs.Tab value="branding">Brändäys</Tabs.Tab>
                        <Tabs.Tab value="location">Sijainti</Tabs.Tab>
                        <Tabs.Tab value="pricing">Hinnat</Tabs.Tab>
                        <Tabs.Tab value="payment">Maksut</Tabs.Tab>
                        <Tabs.Tab value="contacts">Yhteystiedot</Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="basic" pt="md">
                        <Stack gap="md">
                            <Group grow>
                                <TextInput
                                    label="Tapahtuman nimi"
                                    name="eventName"
                                    value={localSettings.eventName ?? ''}
                                    onChange={(e) => handleFieldChange('eventName', e.target.value)}
                                />
                                <NumberInput
                                    label="Paikkoja"
                                    min={0}
                                    name="total"
                                    value={localSettings.total ?? 0}
                                    onChange={(value) => handleFieldChange('total', Number(value ?? 0))}
                                />
                            </Group>
                            <Group grow>
                                <TextInput
                                    label="Alkaa"
                                    type="date"
                                    name="startDate"
                                    value={localSettings.startDate ?? ''}
                                    onChange={(e) => handleFieldChange('startDate', e.target.value)}
                                />
                                <TextInput
                                    label="Päättyy"
                                    type="date"
                                    name="endDate"
                                    value={localSettings.endDate ?? ''}
                                    onChange={(e) => handleFieldChange('endDate', e.target.value)}
                                />
                            </Group>
                            <Textarea
                                label="Ilmoitus"
                                name="announcement"
                                    value={localSettings.announcement ?? ''}
                                    onChange={(e) => handleFieldChange('announcement', e.target.value)}
                                placeholder="Sisäinen muistio tai tiedote järjestäjille"
                            />
                            <Group grow>
                                <Switch
                                    label="Ilmoittautuminen auki"
                                    checked={localSettings.registrationEnabled ?? true}
                                    onChange={(e) =>
                                        handleFieldChange('registrationEnabled', e.currentTarget.checked)
                                    }
                                />
                                <Switch
                                    label="Pöytävaraukset käytössä"
                                    checked={localSettings.bookingEnabled ?? true}
                                    onChange={(e) => handleFieldChange('bookingEnabled', e.currentTarget.checked)}
                                />
                            </Group>
                            <Group grow>
                                <Switch
                                    label="Osallistumispäivät käytössä"
                                    checked={localSettings.attendancePerDayEnabled ?? false}
                                    onChange={(e) =>
                                        handleFieldChange('attendancePerDayEnabled', e.currentTarget.checked)
                                    }
                                />
                                <Switch
                                    label="Ruokailu käytössä"
                                    checked={localSettings.foodEnabled ?? true}
                                    onChange={(e) => handleFieldChange('foodEnabled', e.currentTarget.checked)}
                                />
                            </Group>
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="branding" pt="md">
                        <Stack gap="md">
                            <TextInput
                                label="Sovelluksen otsikko (Intra)"
                                name="appTitle"
                                value={localSettings.appTitle ?? ''}
                                onChange={(e) => handleFieldChange('appTitle', e.target.value)}
                                placeholder="Esim. Tammilanit Intra"
                            />
                            <TextInput
                                label="Järjestäjän nimi"
                                name="organizerName"
                                value={localSettings.organizerName ?? ''}
                                onChange={(e) => handleFieldChange('organizerName', e.target.value)}
                                placeholder="Esim. Tammilanit-yhteisö"
                            />
                            <TextInput
                                label="Logo polku"
                                name="logoPath"
                                value={localSettings.logoPath ?? ''}
                                onChange={(e) => handleFieldChange('logoPath', e.target.value)}
                                placeholder="Esim. assets/logo.png"
                            />
                            <TextInput
                                label="Verkkosivun URL"
                                name="linksWebsite"
                                value={localSettings.linksWebsite ?? ''}
                                onChange={(e) => handleFieldChange('linksWebsite', e.target.value)}
                                placeholder="Esim. https://tammilan.it"
                            />
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="location" pt="md">
                        <Stack gap="md">
                            <TextInput
                                label="Tapahtumapaikan nimi"
                                name="venueName"
                                value={localSettings.venueName ?? ''}
                                onChange={(e) => handleFieldChange('venueName', e.target.value)}
                                placeholder="Esim. Porin helluntaikirkko"
                            />
                            <Textarea
                                label="Osoite"
                                name="venueAddress"
                                value={localSettings.venueAddress ?? ''}
                                onChange={(e) => handleFieldChange('venueAddress', e.target.value)}
                                placeholder="Esim. Itsenäisyydenkatu 9, 28100 Pori"
                                rows={2}
                            />
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="pricing" pt="md">
                        <Stack gap="md">
                            <Group grow>
                                <TextInput
                                    label="Peruspaikan nimi"
                                    name="pricingStandardSeatLabel"
                                    value={localSettings.pricingStandardSeatLabel ?? ''}
                                    onChange={(e) => handleFieldChange('pricingStandardSeatLabel', e.target.value)}
                                    placeholder="Peruspaikka"
                                />
                                <NumberInput
                                    label="Peruspaikan hinta (€)"
                                    min={0}
                                    name="pricingStandardSeatPrice"
                                    value={localSettings.pricingStandardSeatPrice ?? 0}
                                    onChange={(value) => handleFieldChange('pricingStandardSeatPrice', Number(value ?? 0))}
                                />
                            </Group>
                            <TextInput
                                label="Peruspaikan mitat"
                                name="pricingStandardSeatDimensions"
                                value={localSettings.pricingStandardSeatDimensions ?? ''}
                                onChange={(e) => handleFieldChange('pricingStandardSeatDimensions', e.target.value)}
                                placeholder="Esim. 100x75cm"
                            />
                            <Divider />
                            <Group grow>
                                <TextInput
                                    label="Premium-paikan nimi"
                                    name="pricingPremiumSeatLabel"
                                    value={localSettings.pricingPremiumSeatLabel ?? ''}
                                    onChange={(e) => handleFieldChange('pricingPremiumSeatLabel', e.target.value)}
                                    placeholder="Siunattu paikka"
                                />
                                <NumberInput
                                    label="Premium-paikan hinta (€)"
                                    min={0}
                                    name="pricingPremiumSeatPrice"
                                    value={localSettings.pricingPremiumSeatPrice ?? 0}
                                    onChange={(value) => handleFieldChange('pricingPremiumSeatPrice', Number(value ?? 0))}
                                />
                            </Group>
                            <TextInput
                                label="Premium-paikan mitat"
                                name="pricingPremiumSeatDimensions"
                                value={localSettings.pricingPremiumSeatDimensions ?? ''}
                                onChange={(e) => handleFieldChange('pricingPremiumSeatDimensions', e.target.value)}
                                placeholder="Esim. 120x80cm"
                            />
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="payment" pt="md">
                        <Stack gap="md">
                            <TextInput
                                label="MobilePay numero"
                                name="paymentMobilePayNumber"
                                value={localSettings.paymentMobilePayNumber ?? ''}
                                onChange={(e) => handleFieldChange('paymentMobilePayNumber', e.target.value)}
                                placeholder="Esim. +358 45 346 2128"
                            />
                            <TextInput
                                label="Tilinumero (IBAN)"
                                name="paymentBankAccount"
                                value={localSettings.paymentBankAccount ?? ''}
                                onChange={(e) => handleFieldChange('paymentBankAccount', e.target.value)}
                                placeholder="Esim. FI41 1807 2961 1628 14"
                            />
                            <TextInput
                                label="Tilin haltija"
                                name="paymentBankAccountHolder"
                                value={localSettings.paymentBankAccountHolder ?? ''}
                                onChange={(e) => handleFieldChange('paymentBankAccountHolder', e.target.value)}
                                placeholder="Esim. Riku Kuusisto"
                            />
                            <Textarea
                                label="Maksuohjeet"
                                name="paymentInstructions"
                                value={localSettings.paymentInstructions ?? ''}
                                onChange={(e) => handleFieldChange('paymentInstructions', e.target.value)}
                                placeholder="Lisätiedot maksamisesta"
                                rows={3}
                            />
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="contacts" pt="md">
                        <Stack gap="md">
                            <Group justify="space-between">
                                <Text fw={500}>Yhteystiedot</Text>
                                <Button
                                    size="xs"
                                    leftSection={<IconPlus size={14} />}
                                    onClick={handleAddContact}
                                >
                                    Lisää yhteystieto
                                </Button>
                            </Group>
                            {(localSettings.contacts || []).map((contact, index) => (
                                <Box key={index} p="md" style={{ border: '1px solid #dee2e6', borderRadius: '4px' }}>
                                    <Group justify="space-between" mb="xs">
                                        <Text fw={500}>Yhteystieto {index + 1}</Text>
                                        <ActionIcon
                                            color="red"
                                            variant="subtle"
                                            onClick={() => handleRemoveContact(index)}
                                        >
                                            <IconTrash size={16} />
                                        </ActionIcon>
                                    </Group>
                                    <Stack gap="xs">
                                        <TextInput
                                            label="Rooli"
                                            value={contact.role ?? ''}
                                            onChange={(e) => handleContactChange(index, 'role', e.target.value)}
                                            placeholder="Esim. coordinator, technical, payment"
                                        />
                                        <TextInput
                                            label="Nimi"
                                            value={contact.name ?? ''}
                                            onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                                            required
                                        />
                                        <Group grow>
                                            <TextInput
                                                label="Puhelin"
                                                value={contact.phone ?? ''}
                                                onChange={(e) => handleContactChange(index, 'phone', e.target.value)}
                                            />
                                            <TextInput
                                                label="Sähköposti"
                                                type="email"
                                                value={contact.email ?? ''}
                                                onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                                            />
                                        </Group>
                                        <Textarea
                                            label="Muistiinpanot"
                                            value={contact.notes ?? ''}
                                            onChange={(e) => handleContactChange(index, 'notes', e.target.value)}
                                            rows={2}
                                        />
                                        <Group grow>
                                            <NumberInput
                                                label="Järjestys"
                                                value={contact.displayOrder ?? index + 1}
                                                onChange={(value) => handleContactChange(index, 'displayOrder', Number(value ?? index + 1))}
                                            />
                                            <Switch
                                                label="Julkinen"
                                                checked={contact.isPublic ?? true}
                                                onChange={(e) => handleContactChange(index, 'isPublic', e.currentTarget.checked)}
                                            />
                                        </Group>
                                    </Stack>
                                </Box>
                            ))}
                            {(!localSettings.contacts || localSettings.contacts.length === 0) && (
                                <Alert color="blue">
                                    Ei yhteystietoja. Lisää yhteystietoja yllä olevalla napilla.
                                </Alert>
                            )}
                        </Stack>
                    </Tabs.Panel>
                </Tabs>
                <Container >
                <Button
                    color="blue"
                    leftSection={<IconDeviceFloppy size={16} />}
                    type="submit"
                    loading={isOperating('settings')}
                    disabled={isOperating('settings')}
                    mt="md"
                    style={{ width: '100%' }}
                >
                    Tallenna asetukset
                </Button>
                </Container>
                
            </form>
            {lanSettings && (
                <Alert icon={<IconInfoCircle size={16} />} color="blue" style={{ marginTop: '1rem' }}>
                    <Title order={5} mb="xs">Viimeisin versio</Title>
                    <Text size="sm">
                        Luotu {formatDate(lanSettings.createdAt)} • Päivitetty {formatDate(lanSettings.updatedAt)} •
                        ID {lanSettings.id ?? '–'}
                    </Text>
                </Alert>
            )}
        </Stack>
    );
};

export default memo(LanSettingsForm);

